// Button.tsx
import React, { useState, useRef, useEffect } from 'react'
import classes from './Button.module.sass'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectDarkTheme } from '../../../store/slices/settingsSlice'

type Props = {
  text: string
  onClick?: () => void | Promise<void>
  disabled?: boolean
  href?: string
  small?: boolean
  important?: boolean // обратная совместимость
  transparent?: boolean
  blank?: boolean
  timeoutMs?: number
  variant?: 'primary' | 'secondary' | 'danger' // новый пропс
}

export const Button = ({
  text,
  disabled,
  onClick,
  href,
  small,
  important,
  transparent,
  blank,
  timeoutMs = 0,
  variant = 'primary',
}: Props) => {
  const darkTheme = useSelector(selectDarkTheme)
  const [isBlocked, setIsBlocked] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Определяем класс кнопки с учётом обратной совместимости
  const getButtonClass = () => {
    // Приоритет: variant > important > дефолт
    if (variant === 'danger') {
      return transparent && small ? classes.ContainerOutlinedSmallDanger : classes.ContainerDanger
    }
    if (variant === 'secondary' || important) {
      return transparent && small
        ? classes.ContainerOutlinedSmallSecondary
        : classes.ContainerImportant
    }
    // primary — дефолт
    if (transparent && small) {
      return classes.ContainerOutlinedSmallPrimary
    }
    if (small) {
      return classes.ContainerSmall
    }
    return classes.Container
  }

  const handleClick = async (e: React.MouseEvent) => {
    if (href) return

    e.preventDefault()

    if (isBlocked || disabled || !onClick) {
      return
    }

    setIsBlocked(true)

    try {
      const result = onClick()
      if (result && typeof result.then === 'function') {
        await result
      }
    } catch (error) {
      console.error('Error in button handler:', error)
      throw error
    } finally {
      timeoutRef.current = setTimeout(() => {
        setIsBlocked(false)
        timeoutRef.current = null
      }, timeoutMs)
    }
  }

  const buttonClass = getButtonClass()

  if (href) {
    return (
      <Link
        to={href}
        style={{ width: '100%', textDecoration: 'none' }}
        target={blank ? '_blank' : ''}
      >
        <button className={buttonClass} disabled={disabled}>
          {text}
        </button>
      </Link>
    )
  }

  return (
    <button className={buttonClass} disabled={disabled} onClick={handleClick}>
      {text}
    </button>
  )
}